import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { ViewTypesList } from '@features/scheduler/scheduler-container/viev-types.enum';
import { TimeService } from '@services/common/time.service';
import { FilterTypeFields, PeriodTypes, PeriodTypesListOptions } from '@shared/components/scheduler-period-picker/filterTypes';
import { PeriodCalendarService } from '@shared/components/scheduler-period-picker/period-picker-calendar/period-picker-calendar.service';
import { FilterStorageModel } from '@shared/interfaces/filtersStore.model';
import {
  formatDate,
  getEndOf2Week,
  getEndOf4Week,
  getEndOfMonth,
  getEndOfWeek,
  getStartOfMonth,
  getStartOfWeek
} from '@shared/timetracking-filter/date.utility';
import { addDays, addMonths, addWeeks, getDaysInMonth, setDate, subDays, subMonths, subWeeks } from 'date-fns';

import { legacyParse } from '@date-fns/upgrade/v2';

interface PeriodDateObj {
  fromDate: string;
  toDate: string;
  type: string;
  period: string;
}

@Component({
  selector: 'scheduler-period-picker',
  templateUrl: './scheduler-period-picker.component.html',
  styleUrls: ['./scheduler-period-picker.component.scss'],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: PeriodCalendarService
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SchedulerPeriodPickerComponent {
  @Input() filters: FilterStorageModel;
  @Input() set fromDate(date) {
    if (date) {
      this._fromDate = date;
    }
  }
  @Input() set toDate(date) {
    if (date) {
      this._toDate = date;
    }
  }
  @Input() period: string;
  @Input() includeOption = [];
  @Input() disabled: boolean;
  @Input() showPeriod: boolean = true;
  @Input() isMobile: boolean = false;
  @Output() updatedFilters = new EventEmitter();

  get fromDate() {
    return this._fromDate;
  }
  get toDate() {
    return this._toDate;
  }

  get periodTypesList() {
    return PeriodTypesListOptions.filter((item) => {
      return this.includeOption.length ? this.includeOption.includes(item.value) : item;
    });
  }
  filterTypesEnum = FilterTypeFields;
  viewTypesEnum = ViewTypesList;
  _fromDate;
  _toDate;

  get periodTypeModel() {
    return this.periodTypesList.find((item) => item.value === this.period);
  }

  updatePeriod(data, type?: string): void {
    const model = type === 'picker' ? data : data.model;
    this.fromDate = model.fromDate;
    this.toDate = model.toDate;
    this.period = type === 'picker' ? model.period : model.value;

    const periodDateObject = this.periodFactory(this.fromDate, this.toDate, this.period);
    this.updatedFilters.next({ items: periodDateObject });
  }

  updatePeriodDate(e: Event, option?) {
    e.stopPropagation();
    e.preventDefault();

    const periodType = this.periodTypeModel.value;
    const fromDate = new Date(this.fromDate);

    let periodDateObject = {
      fromDate: null,
      toDate: null,
      type: '',
      period: ''
    };

    switch (periodType) {
      case PeriodTypes.Day:
        const fromTimeDay = option === '+' ? addDays(legacyParse(fromDate), 1) : subDays(legacyParse(fromDate), 1);
        const formatDayFromTime = this._timeService.formatDate(fromTimeDay);

        periodDateObject = this.periodFactory(formatDayFromTime, formatDayFromTime, periodType);
        break;
      case PeriodTypes.Week:
        const weekToTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.toDate)), 1) : subWeeks(legacyParse(this.toDate), 1);
        const weekFromTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.fromDate)), 1) : subWeeks(legacyParse(this.fromDate), 1);
        const formatWeekToTime = this._timeService.formatDate(weekToTime);
        const formatWeekFromTime = this._timeService.formatDate(weekFromTime);

        periodDateObject = this.periodFactory(formatWeekFromTime, formatWeekToTime, periodType);
        break;
      case PeriodTypes.TwoWeeks:
        const twoWeekToTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.toDate)), 2) : subWeeks(legacyParse(this.toDate), 2);
        const twoWeekFromTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.fromDate)), 2) : subWeeks(legacyParse(this.fromDate), 2);
        const formatTwoWeekToTime = this._timeService.formatDate(twoWeekToTime);
        const formatTwoWeekFromTime = this._timeService.formatDate(twoWeekFromTime);

        periodDateObject = this.periodFactory(formatTwoWeekFromTime, formatTwoWeekToTime, periodType);
        break;
      case PeriodTypes.FourWeeks:
        const fourWeekToTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.toDate)), 4) : subWeeks(legacyParse(this.toDate), 4);
        const fourWeekFromTime =
          option === '+' ? addWeeks(legacyParse(new Date(this.fromDate)), 4) : subWeeks(legacyParse(this.fromDate), 4);
        const formatFourWeekToTime = this._timeService.formatDate(fourWeekToTime);
        const formatFourWeekFromTime = this._timeService.formatDate(fourWeekFromTime);

        periodDateObject = this.periodFactory(formatFourWeekFromTime, formatFourWeekToTime, periodType);
        break;
      case PeriodTypes.Month:
        const toTime =
          option === '+' ? addMonths(legacyParse(new Date(this.toDate)), 1) : subMonths(legacyParse(new Date(this.toDate)), 1);
        const fromTime =
          option === '+'
            ? addMonths(legacyParse(new Date(this.fromDate)), 1)
            : subMonths(legacyParse(new Date(this.fromDate)), 1);
        const daysInMonth = getDaysInMonth(legacyParse(toTime));
        const formatMonthToTime = this._timeService.formatDate(setDate(legacyParse(toTime), daysInMonth));
        const formatMonthFromTime = this._timeService.formatDate(setDate(legacyParse(fromTime), 1));

        periodDateObject = this.periodFactory(formatMonthFromTime, formatMonthToTime, periodType);

        break;
    }

    this.initSelectedPeriod(periodType);

    this._fromDate = periodDateObject.fromDate;
    this._toDate = periodDateObject.toDate;

    this.updatedFilters.next({ items: periodDateObject });
  }

  resetPeriodToToday(e: Event) {
    e.stopPropagation();
    e.preventDefault();

    const periodType = this.periodTypeModel.value;

    let periodDateObject = {};

    switch (periodType) {
      case PeriodTypes.Day:
        periodDateObject = this.periodFactory(formatDate(), formatDate(), periodType);
        break;
      case PeriodTypes.Week:
        periodDateObject = this.periodFactory(getStartOfWeek(), getEndOfWeek(), periodType);
        break;
      case PeriodTypes.TwoWeeks:
        periodDateObject = this.periodFactory(getStartOfWeek(), getEndOf2Week(), periodType);
        break;
      case PeriodTypes.FourWeeks:
        periodDateObject = this.periodFactory(getStartOfWeek(), getEndOf4Week(), periodType);
        break;
      case PeriodTypes.Month:
        periodDateObject = this.periodFactory(getStartOfMonth(), getEndOfMonth(), periodType);
        break;
    }

    this.updatedFilters.next({ items: periodDateObject });
  }

  private initSelectedPeriod(value?): void {
    this._periodCalendarService.selectedPeriod$.next(value ? value : this.period);
  }

  private periodFactory(from, to, periodType): PeriodDateObj {
    return {
      fromDate: from,
      toDate: to,
      type: 'period',
      period: periodType
    };
  }

  constructor(
    private _timeService: TimeService,
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private _periodCalendarService: PeriodCalendarService<Date>
  ) {}
}
